import { NgTemplateOutlet } from "@angular/common";
import {
    Component,
    InputSignal,
    ModelSignal,
    OutputEmitterRef,
    Signal,
    TemplateRef,
    computed,
    contentChild,
    input,
    model,
    output,
} from "@angular/core";
import { DialogModule } from "primeng/dialog";

type ModalType = "default" | "confirm";

@Component({
    selector: "ui-modal",
    imports: [DialogModule, NgTemplateOutlet],
    templateUrl: "./modal.component.html",
    styleUrl: "./modal.component.css",
})
/**
 * Reusable UI dialog/modal component wrapping PrimeNG Dialog with custom header, footer, and confirmation themes.
 * Supports body appending, scroll blocking, and custom dismiss behaviors.
 */
export class ModalComponent {
    /** Two-way model binding signal controlling modal visibility. */
    public readonly isVisible: ModelSignal<boolean> = model.required();

    /** Modal theme type variant (`default` or `confirm`). */
    public readonly type: InputSignal<ModalType> = input<ModalType>("default");

    /** Whether the backdrop behind the modal should be blurred. */
    public readonly isBackdropBlurred: InputSignal<boolean> = input(false);

    /** Custom CSS style class applied to modal wrapper. */
    public readonly customStyleClass: InputSignal<string> = input("");

    /** Computed CSS class string built from modal type, body append option, and custom classes. */
    protected readonly styleClass: Signal<string> = computed(() => {
        const classes: string[] = ["modal"];

        // Modal type changes the outer layout style.
        switch (this.type()) {
            case "default":
                classes.push("modal_type_default");
                break;
            case "confirm":
                classes.push("modal_type_confirm");
                break;
        }

        if (this.isAppendedToBody()) {
            classes.push("modal_append_body");
        }

        classes.push(this.customStyleClass());

        if (this.isBackdropBlurred()) {
            classes.push("modal_backdrop_blur");
        }

        return classes.join(" ");
    });

    /** Plain text header title displayed in modal header. */
    public readonly header: InputSignal<string> = input("");
    /** TemplateRef contentChild signal for custom header slot projection (`#modalHeader`). */
    public readonly headerRef: Signal<TemplateRef<unknown> | undefined> = contentChild("modalHeader", {
        descendants: true,
    });
    /** Computed flag determining whether header section is visible. */
    protected readonly showHeader: Signal<boolean> = computed(() => !!this.header() || !!this.headerRef());

    /** Whether close button is enabled. */
    public readonly closable: InputSignal<boolean> = input(true);
    /** Computed flag determining whether to show close icon button. */
    protected readonly showCloseButton: Signal<boolean> = computed(() => this.closable() && !this.headerRef());
    /** Whether clicking overlay mask dismisses modal. */
    public readonly dismissableMask: InputSignal<boolean> = input(false);
    /** Whether modal element should be appended to body document. */
    public readonly isAppendedToBody: InputSignal<boolean> = input(false);

    /** Whether background scrolling is blocked when modal is open. */
    public readonly blockScroll: InputSignal<boolean> = input(true);
    /** Whether to automatically focus the first focusable element when modal is shown. */
    public readonly focusOnShow: InputSignal<boolean> = input(true);

    /** Plain text content string displayed inside modal body. */
    public readonly modalText: InputSignal<string> = input("");

    /** Event emitted when modal finishes opening. */
    public readonly onShow: OutputEmitterRef<void> = output();
    /** Event emitted when modal finishes closing. */
    public readonly onHide: OutputEmitterRef<void> = output();

    /** TemplateRef contentChild signal for custom footer slot projection (`#modalFooter`). */
    public readonly footerRef: Signal<TemplateRef<unknown> | undefined> = contentChild("modalFooter", {
        descendants: true,
    });

    /** Event handler callback triggered on modal show event. */
    protected handleShow(): void {
        this.onShow.emit();
    }

    /** Event handler callback triggered on modal hide event. */
    protected handleHide(): void {
        this.onHide.emit();
    }
}
